import { useState } from "react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Pencil, ListChecks, UserX, UserCheck } from "lucide-react";

/** Three-dot actions for a practitioner row in Settings → Team.
 *  Rendered through the Radix portal so the card's overflow can never clip it,
 *  and it flips above the trigger when there is no room below. */
export const PractitionerRowMenu = ({ member, canManage, isSelf, onEditProfile, onEditTypes, onStatusChanged }) => {
  const [busy, setBusy] = useState(false);

  const changeStatus = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/practitioners/${member.id}/${action}`);
      onStatusChanged?.(data.message);
    } catch (err) {
      const d = err.response?.data?.detail;
      onStatusChanged?.(typeof d === "string" ? d : d?.message || "That change could not be saved.");
    }
    setBusy(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`practitioner-menu-${member.id}`} aria-label="Practitioner actions"
          className="p-1.5 rounded-lg text-[#757675] hover:bg-[#1A1B1A]/[0.04] hover:text-[#1A1B1A] outline-none">
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} collisionPadding={12}
        className="w-56 bg-white border-[#1A1B1A]/[0.08] shadow-lg rounded-xl p-1"
        data-testid={`practitioner-menu-items-${member.id}`}>
        {(canManage || isSelf) && (
          <DropdownMenuItem onSelect={onEditProfile} data-testid={`menu-edit-practitioner-${member.id}`}
            className="text-xs text-[#4A4B4A] gap-2 cursor-pointer focus:bg-[#FAF9F6]">
            <Pencil className="w-3.5 h-3.5" /> Edit practitioner
          </DropdownMenuItem>
        )}
        {canManage && (
          <DropdownMenuItem onSelect={onEditTypes} data-testid={`menu-edit-types-${member.id}`}
            className="text-xs text-[#4A4B4A] gap-2 cursor-pointer focus:bg-[#FAF9F6]">
            <ListChecks className="w-3.5 h-3.5" /> Edit appointment types
          </DropdownMenuItem>
        )}
        {canManage && member.treats_patients && (
          member.is_active_practitioner ? (
            <DropdownMenuItem disabled={busy} onSelect={() => changeStatus("deactivate")}
              data-testid={`menu-deactivate-${member.id}`}
              className="text-xs text-red-600 gap-2 cursor-pointer focus:bg-red-50 focus:text-red-600">
              <UserX className="w-3.5 h-3.5" /> Deactivate practitioner
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled={busy} onSelect={() => changeStatus("reactivate")}
              data-testid={`menu-reactivate-${member.id}`}
              className="text-xs text-[#205B5A] gap-2 cursor-pointer focus:bg-[#205B5A]/[0.06] focus:text-[#205B5A]">
              <UserCheck className="w-3.5 h-3.5" /> Reactivate practitioner
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
